import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronLeft, Bookmark, BookmarkCheck, Heart, MessageCircle } from "lucide-react";
import { setPendingSave } from "@/lib/pendingSave";
import MaterialArticleView from "@/components/MaterialArticleView";
import QuestionDialog from "@/components/QuestionDialog";
import MaterialDiscussion from "@/components/MaterialDiscussion";
import useMaterialAnalytics from "@/lib/useMaterialAnalytics";

export default function MaterialDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [material, setMaterial] = useState(null);
  const contentRef = useRef(null);
  useMaterialAnalytics(material?.id, contentRef);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saved, setSaved] = useState(false);
  const [liked, setLiked] = useState(false);
  const [askOpen, setAskOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/library/materials/${slug}`);
      setMaterial(data);
      setNotFound(false);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
      else toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!user || !material) return;
    (async () => {
      try {
        const [s, l] = await Promise.all([api.get("/account/saved"), api.get("/account/likes/ids")]);
        setSaved(s.data.some((m) => m.id === material.id));
        setLiked(l.data.includes(material.id));
      } catch { /* noop */ }
    })();
  }, [user, material]);

  const toggleLike = async () => {
    if (!material) return;
    if (!user) { toast("Войдите, чтобы отметить понравившееся"); nav("/login"); return; }
    try {
      if (liked) { await api.delete(`/account/likes/${material.id}`); setLiked(false); }
      else { await api.post("/account/likes", { material_id: material.id }); setLiked(true); }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleSave = async () => {
    if (!material) return;
    if (!user) {
      setPendingSave(material.id);
      toast("Войдите, чтобы сохранить материал", {
        description: "После входа мы автоматически добавим его в «Сохранённое».",
      });
      nav("/login");
      return;
    }
    try {
      if (saved) {
        await api.delete(`/account/saved/${material.id}`);
        setSaved(false);
        toast.success("Удалено из «Сохранённое»");
      } else {
        await api.post("/account/saved", { material_id: material.id });
        setSaved(true);
        toast.success("Сохранено в «Сохранённое»");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream" data-testid="material-detail-loading">
        <div className="max-w-3xl mx-auto px-5 py-10">
          <div className="h-6 w-24 bg-sand/40 rounded animate-pulse" />
          <div className="h-10 w-3/4 bg-sand/40 rounded animate-pulse mt-4" />
          <div className="h-64 bg-sand/30 rounded-3xl animate-pulse mt-6" />
        </div>
      </div>
    );
  }
  if (notFound || !material) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center px-5" data-testid="material-detail-notfound">
        <p className="font-serif text-2xl text-espresso">Материал не найден</p>
        <p className="text-sm text-muted-foreground mt-2">
          Возможно, он ещё не опубликован или был снят с публикации.
        </p>
        <Link to="/library" className="chip bg-espresso text-white mt-6 px-5 py-2">
          В библиотеку
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="material-detail-page">
      <header className="max-w-3xl mx-auto px-5 pt-5 flex items-center gap-3">
        <button
          onClick={() => nav("/library")}
          data-testid="back-to-library-button"
          className="p-2 -ml-2 rounded-full hover:bg-sand/50"
          aria-label="В библиотеку"
        >
          <ChevronLeft className="w-5 h-5 text-espresso" />
        </button>
      </header>

      <MaterialArticleView material={material} testidPrefix="material" contentRef={contentRef} />

      <div className="max-w-3xl mx-auto px-5 pb-16 -mt-8">
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={toggleSave}
            data-testid="material-save-button"
            aria-pressed={saved}
            className={`chip px-5 py-2 border transition-colors flex items-center gap-2 ${
              saved
                ? "bg-espresso text-white border-espresso"
                : "bg-white text-espresso border-[#E1D2B8] hover:bg-sand/40"
            }`}
          >
            {saved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
            {saved ? "Сохранено" : "Сохранить"}
          </button>
          <button
            onClick={toggleLike}
            data-testid="material-like-button"
            aria-pressed={liked}
            className={`chip px-5 py-2 border transition-colors flex items-center gap-2 ${
              liked
                ? "bg-red-50 text-red-500 border-red-200"
                : "bg-white text-espresso border-[#E1D2B8] hover:bg-sand/40"
            }`}
          >
            <Heart className={`w-4 h-4 ${liked ? "fill-red-500" : ""}`} />
            {liked ? "Нравится" : "В понравившиеся"}
          </button>
          <button
            onClick={() => {
              if (!user) { toast("Войдите, чтобы задать вопрос"); nav("/login"); return; }
              setAskOpen(true);
            }}
            data-testid="material-ask-button"
            className="chip px-5 py-2 border transition-colors flex items-center gap-2 bg-white text-espresso border-[#E1D2B8] hover:bg-sand/40"
          >
            <MessageCircle className="w-4 h-4" />
            Задать вопрос
          </button>
        </div>
        <MaterialDiscussion material={material} />
      </div>
      <QuestionDialog open={askOpen} onOpenChange={setAskOpen} material={material} />
    </div>
  );
}
